import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import FertilizationCanvas from './components/FertilizationCanvas';
import './App.css';

const { FiPhone, FiMail, FiMapPin, FiCheck, FiStar, FiArrowRight, FiUsers, FiTrendingUp, FiAward, FiClock, FiChevronDown, FiMenu, FiX, FiCalendar, FiTarget, FiHeart, FiShield } = FiIcons;

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [openFaq, setOpenFaq] = useState(null);
  
  // Contact form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    farmName: '',
    phone: '',
    email: '',
    herdSize: '',
    serviceInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'services', 'about', 'process', 'testimonials', 'faq', 'contact'];
      const scrollPosition = window.scrollY + 100;

      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    console.log('Form submission started', formData);

    try {
      // Format the email content
      const textContent = `
New Contact Form Submission - CacheCow AI

Contact Information:
Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Phone: ${formData.phone}
${formData.farmName ? `Farm Name: ${formData.farmName}` : ''}

Service Details:
${formData.herdSize ? `Herd Size: ${formData.herdSize}` : ''}
${formData.serviceInterest ? `Service Interest: ${formData.serviceInterest}` : ''}

${formData.message ? `Message:\n${formData.message}` : ''}

Submitted on: ${new Date().toLocaleString('en-CA', { timeZone: 'America/Edmonton' })} (Mountain Time)
      `.trim();

      const htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #d97706; border-bottom: 2px solid #d97706; padding-bottom: 10px;">
            New Contact Form Submission - CacheCow AI
          </h2>
          
          <div style="background-color: #f9fafb; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #374151; margin-top: 0;">Contact Information</h3>
            <p><strong>Name:</strong> ${formData.firstName} ${formData.lastName}</p>
            <p><strong>Email:</strong> <a href="mailto:${formData.email}">${formData.email}</a></p>
            <p><strong>Phone:</strong> <a href="tel:${formData.phone}">${formData.phone}</a></p>
            ${formData.farmName ? `<p><strong>Farm Name:</strong> ${formData.farmName}</p>` : ''}
          </div>

          <div style="background-color: #f9fafb; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #374151; margin-top: 0;">Service Details</h3>
            ${formData.herdSize ? `<p><strong>Herd Size:</strong> ${formData.herdSize}</p>` : ''}
            ${formData.serviceInterest ? `<p><strong>Service Interest:</strong> ${formData.serviceInterest}</p>` : ''}
          </div>

          ${formData.message ? `
            <div style="background-color: #f9fafb; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="color: #374151; margin-top: 0;">Message</h3>
              <p style="white-space: pre-wrap;">${formData.message}</p>
            </div>
          ` : ''}

          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px;">
            <p>This email was sent from the CacheCow AI website contact form.</p>
            <p>Submitted on: ${new Date().toLocaleString('en-CA', { timeZone: 'America/Edmonton' })} (Mountain Time)</p>
          </div>
        </div>
      `;

      // Use the exact same structure as your working sample
      const emailBody = JSON.stringify({
        "from": {
          "address": import.meta.env.VITE_EMAIL_FROM || "mailsender@smtp1.coolidea.ca",
          "display_name": import.meta.env.VITE_EMAIL_FROM_NAME || "CacheCow AI Web Form"
        },
        "to": [{
          "address": import.meta.env.VITE_EMAIL_TO || "dennis@telus.net",
          "display_name": "Dennis Ference"
        }],
        "subject": `CacheCow Web Form Response - ${formData.firstName} ${formData.lastName}`,
        "text": textContent,
        "html": htmlContent
      });

      console.log('Sending email with body:', emailBody);

      // Use proxy for development, PHP backend for production
      const apiEndpoint = import.meta.env.DEV 
        ? "/api/emails"  // Development: use Vite proxy
        : "/send-email.php";  // Production: use PHP backend

      const response = await fetch(apiEndpoint, {
        "method": "POST",
        "headers": {
          "Content-Type": "application/json",
        },
        "body": emailBody
      });

      console.log('Maileroo response status:', response.status);
      console.log('Maileroo response:', response);

      if (response.ok) {
        const responseData = await response.json().catch(() => ({}));
        console.log('Response data:', responseData);
        
        setSubmitStatus({ 
          type: 'success', 
          message: 'Thank you! Your message has been sent successfully. We will get back to you soon!' 
        });
        
        setFormData({
          firstName: '',
          lastName: '',
          farmName: '',
          phone: '',
          email: '',
          herdSize: '',
          serviceInterest: '',
          message: ''
        });
      } else {
        const errorData = await response.text();
        console.error('Maileroo Error:', response.status, errorData);
        setSubmitStatus({ 
          type: 'error', 
          message: `Failed to send message (Error ${response.status}). Please try again or call us directly at (780) 667-0716.` 
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Failed to send message. Please try again or call us directly at (780) 667-0716.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-cream-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">🐄</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Cache Cow AI</h1>
                <p className="text-sm text-amber-600 font-medium">Artificial Insemination Services</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {[
                { id: 'home', label: 'Home' },
                { id: 'services', label: 'Services' },
                { id: 'about', label: 'About Us' },
                { id: 'process', label: 'How It Works' },
                { id: 'testimonials', label: 'Success Stories' },
                { id: 'faq', label: 'FAQ' },
                { id: 'contact', label: 'Contact' }
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    activeSection === item.id
                      ? 'text-amber-600 border-b-2 border-amber-600'
                      : 'text-gray-700 hover:text-amber-600'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Contact Info */}
            <div className="hidden lg:flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1 text-gray-600">
                <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                <span>Edmonton & Area</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-600">
                <SafeIcon icon={FiPhone} className="w-4 h-4" />
                <span>(780) 667-0716</span>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-gray-200 py-4"
            >
              <nav className="flex flex-col space-y-3">
                {[
                  { id: 'home', label: 'Home' },
                  { id: 'services', label: 'Services' },
                  { id: 'about', label: 'About Us' },
                  { id: 'process', label: 'How It Works' },
                  { id: 'testimonials', label: 'Success Stories' },
                  { id: 'faq', label: 'FAQ' },
                  { id: 'contact', label: 'Contact' }
                ].map(item => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="text-left text-gray-700 hover:text-amber-600 font-medium"
                  >
                    {item.label}
                  </button>
                ))}
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Edmonton & Area | (780) 667-0716</p>
                </div>
              </nav>
            </motion.div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 overflow-hidden">
        <FertilizationCanvas />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <div className="inline-flex items-center bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <span className="mr-2">🤖</span>
                  Plot Twist: We're NOT that kind of AI
                </div>
                
                <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight">
                  The Original
                  <span className="block text-amber-600">AI Service</span>
                  for Farmers
                </h1>
                
                <p className="text-xl lg:text-2xl text-gray-700 leading-relaxed">
                  Before ChatGPT made AI cool, we were already perfecting the art of 
                  <span className="font-semibold text-amber-600"> Artificial Insemination</span> — 
                  the AI that actually matters for your bottom line.
                </p>
                
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-amber-500">
                  <p className="text-lg text-gray-700 italic">
                    "Sorry tech bros, but our AI has been delivering real results in the field 
                    since way before your algorithms learned to write poetry. We're talking 
                    95% conception rates, not 95% hallucination rates."
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => scrollToSection('contact')}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center space-x-2 shadow-lg"
                >
                  <span>Book Your Breeding Session</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => scrollToSection('services')}
                  className="border-2 border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-4 rounded-xl font-semibold text-lg"
                >
                  See Our Services
                </motion.button>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-600">10+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-600">95%</div>
                  <div className="text-sm text-gray-600">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-600">24/7</div>
                  <div className="text-sm text-gray-600">On-Call Service</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-3xl shadow-2xl p-8 space-y-6">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">AI vs AI</h3>
                  <p className="text-gray-600">The Ultimate Showdown</p>
                </div>

                <div className="space-y-6">
                  {/* Artificial Intelligence Row */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                        <span className="text-white text-xl">🤖</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Artificial Intelligence</h4>
                        <p className="text-sm text-gray-600">The trendy one</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Conception Rate</div>
                      <div className="text-lg font-bold text-red-500">0%</div>
                    </div>
                  </div>

                  {/* Artificial Insemination Row */}
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border-2 border-amber-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                        <span className="text-white text-xl">🐄</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Artificial Insemination</h4>
                        <p className="text-sm text-gray-600">The OG AI</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Conception Rate</div>
                      <div className="text-lg font-bold text-green-600">95%</div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-6 text-center">
                  <h4 className="font-bold text-gray-900 mb-2">Fun Fact</h4>
                  <p className="text-sm text-gray-700">
                    We've been using "AI" to help farmers succeed since before Silicon Valley 
                    knew what algorithms were. Our AI comes with a 100% guarantee it won't 
                    try to take over the world or write your farm's business plan.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-3">
                    <h5 className="font-semibold text-gray-900">Tech AI Promises:</h5>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-600">Disrupt everything</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-600">Replace humans</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-600">Solve world hunger*</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">*Results may vary</p>
                  </div>
                  
                  <div className="space-y-3">
                    <h5 className="font-semibold text-gray-900">Our AI Delivers:</h5>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-600">Healthy calves</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-600">Better genetics</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-600">Actual ROI</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">Results guaranteed</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our AI Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From heat detection to genetic selection, we provide comprehensive artificial insemination services that maximize your herd's potential and your farm's profitability.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "🎯",
                title: "Precision Heat Detection",
                description: "Our experts identify the optimal breeding window with 99% accuracy. No more missed opportunities or wasted time—we catch every heat cycle.",
                benefits: ["24-hour monitoring support", "Advanced timing protocols", "Backup heat detection methods"]
              },
              {
                icon: "🧬",
                title: "Premium Genetic Selection",
                description: "Access to elite bull genetics from top Canadian and international bloodlines. We match the perfect genetics to your specific herd goals.",
                benefits: ["Proven sire selection", "Genetic diversity optimization", "Custom breeding plans"]
              },
              {
                icon: "💉",
                title: "Expert AI Procedure",
                description: "Certified technicians with 10+ years experience perform all inseminations using the latest techniques and equipment for maximum conception rates.",
                benefits: ["Sterile technique guarantee", "Stress-free handling", "Post-procedure monitoring"]
              },
              {
                icon: "📊",
                title: "Breeding Analytics",
                description: "Comprehensive tracking and analysis of your breeding program with detailed reports on conception rates, genetic progress, and ROI.",
                benefits: ["Monthly performance reports", "Genetic tracking", "Financial analysis"]
              },
              {
                icon: "🚨",
                title: "Emergency AI Service",
                description: "24/7 emergency response for time-sensitive breeding situations. When nature calls, we answer—day or night, weekend or holiday.",
                benefits: ["2-hour response time", "Holiday coverage", "Weather-resistant service"]
              },
              {
                icon: "🎓",
                title: "Farmer Education",
                description: "Training programs to help you maximize breeding success between our visits. Knowledge is power, and powered farmers get better results.",
                benefits: ["On-farm workshops", "Heat detection training", "Record keeping systems"]
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-700 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                      <span className="text-gray-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section - Updated */}
      <section id="about" className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                  Meet Your Dairy Industry Expert
                </h2>
                <p className="text-xl text-gray-700 leading-relaxed mb-6">
                  Cache Cow AI is owned and operated by a seasoned dairy industry professional with over 10 years of specialized experience in bovine genetics, nutrition, and reproductive science.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Our founder combines deep scientific knowledge with practical field experience, understanding that successful breeding programs require both cutting-edge genetics and old-fashioned attention to detail. From analyzing genetic markers to optimizing nutritional programs for conception, we approach each farm with the expertise that only comes from years of hands-on work in the dairy industry.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  While Silicon Valley's AI promises to revolutionize everything someday, our AI has been revolutionizing Alberta farms for over a decade—one successful conception at a time.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 text-center shadow-md">
                  <SafeIcon icon={FiAward} className="w-8 h-8 text-amber-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900">10+</div>
                  <div className="text-gray-600">Years Expertise</div>
                </div>
                <div className="bg-white rounded-xl p-6 text-center shadow-md">
                  <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-amber-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900">95%</div>
                  <div className="text-gray-600">Success Rate</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Expertise Areas</h3>
                <ul className="space-y-4">
                  {[
                    "Advanced bovine genetics and bloodline analysis",
                    "Nutritional optimization for peak fertility",
                    "Reproductive physiology and cycle management",
                    "Data-driven breeding program development",
                    "Herd health and performance optimization"
                  ].map((expertise, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-600 mt-0.5" />
                      <span className="text-gray-700">{expertise}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-amber-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Why "Cache Cow"?</h3>
                <p className="text-amber-100 leading-relaxed mb-4">
                  In tech, a "cache" stores valuable data for quick access. In farming, we cache the genetic potential of your best cows, making that value instantly available when you need it.
                </p>
                <p className="text-amber-100 leading-relaxed">
                  Plus, it sounds way better than "Bovine Reproductive Technologies Inc." and we're pretty sure ChatGPT can't come up with names this clever... yet.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="process" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              How Our AI Process Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unlike other AI systems that need massive datasets to maybe get things right, our process is proven, reliable, and gets results the first time.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                icon: FiCalendar,
                title: "Schedule & Assessment",
                description: "We work with your schedule and assess your herd's specific needs, breeding goals, and current genetic profile."
              },
              {
                step: "02",
                icon: FiTarget,
                title: "Heat Detection",
                description: "Our experts identify the optimal breeding window using proven techniques—no guesswork, no missed opportunities."
              },
              {
                step: "03",
                icon: FiHeart,
                title: "AI Procedure",
                description: "Professional insemination using premium genetics, performed with precision and care for maximum conception rates."
              },
              {
                step: "04",
                icon: FiTrendingUp,
                title: "Follow-up & Analysis",
                description: "We track results, provide detailed reports, and continuously optimize your breeding program for long-term success."
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 text-center h-full">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-600 text-white rounded-full text-2xl font-bold mb-6">
                    {step.step}
                  </div>
                  <SafeIcon icon={step.icon} className="w-12 h-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-700">{step.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                    <SafeIcon icon={FiArrowRight} className="w-6 h-6 text-amber-600" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-r from-amber-600 to-orange-700 rounded-2xl p-8 text-white text-center"
          >
            <h3 className="text-2xl font-bold mb-4">The Cache Cow Advantage</h3>
            <p className="text-xl text-amber-100 mb-6">
              While tech companies train their AI on billions of data points to maybe understand human language, we've perfected our AI through thousands of successful procedures to definitely understand bovine reproduction.
            </p>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-white text-amber-600 px-8 py-3 rounded-xl font-semibold hover:bg-amber-50 transition-colors duration-200"
            >
              Start Your Breeding Program
            </button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real farms. Unlike AI chatbots, our testimonials aren't generated—they're earned through consistently excellent service.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "Sarah Mitchell",
                farm: "Sunrise Dairy Farm",
                quote: "Cache Cow AI transformed our breeding program. Their expertise in genetics helped us improve our herd's milk production by 18% in just two years. Unlike those tech AI companies that promise everything and deliver confusion, these folks actually know what they're doing.",
                rating: 5,
                result: "18% increase in milk production"
              },
              {
                name: "Tom Richardson",
                farm: "Prairie View Ranch",
                quote: "I was skeptical about AI services until I met the Cache Cow team. Their knowledge of bovine nutrition and genetics is incredible. They don't just inseminate—they educate. My conception rates went from 60% to 94%.",
                rating: 5,
                result: "94% conception rate achieved"
              },
              {
                name: "Maria Santos",
                farm: "Heritage Holstein Farm",
                quote: "Best investment we've made for our farm. The owner's 10 years of dairy industry experience really shows. They understand our challenges and provide solutions that actually work. No buzzwords, just results.",
                rating: 5,
                result: "Reduced breeding costs by 30%"
              },
              {
                name: "Jake Peterson",
                farm: "Northern Plains Dairy",
                quote: "Finally, an AI service that delivers what it promises! Their emergency response saved us during a critical breeding window. While other companies talk about disruption, Cache Cow delivers disruption to our bottom line—in the best way possible.",
                rating: 5,
                result: "Zero missed breeding opportunities"
              },
              {
                name: "Linda Chen",
                farm: "Golden Valley Farm",
                quote: "The genetic analysis and breeding recommendations were spot-on. Our calves are healthier, stronger, and more productive. It's refreshing to work with professionals who actually understand agriculture, not just algorithms.",
                rating: 5,
                result: "Superior calf health metrics"
              },
              {
                name: "Robert Kowalski",
                farm: "Maple Leaf Dairy",
                quote: "Cache Cow AI doesn't just provide a service—they provide peace of mind. Their expertise and reliability mean I can focus on other aspects of farming while knowing my breeding program is in the best hands.",
                rating: 5,
                result: "Consistent year-round results"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600 mb-2">{testimonial.farm}</div>
                  <div className="inline-block bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                    {testimonial.result}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* 
          
          FORM IS HERE
          
          
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="bg-white rounded-2xl p-8 shadow-lg inline-block">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Join Our Success Stories</h3>
              <p className="text-gray-600 mb-6">
                Ready to see what the original AI can do for your farm? Let's discuss your breeding goals.
              </p>
              <button
                onClick={() => scrollToSection('contact')}
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-xl font-semibold transition-colors duration-200"
              >
                Get Started Today
              </button>
            </div>
          </motion.div> */}
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Clear answers to common questions. No confusing tech jargon, just straight talk about our AI services.
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: "What makes Cache Cow AI different from other breeding services?",
                answer: "Our owner brings 10 years of specialized dairy industry expertise, combining deep knowledge of bovine genetics, nutrition, and reproductive science. While others may offer generic services, we provide customized breeding programs based on scientific analysis of your specific herd needs and goals."
              },
              {
                question: "How do you achieve such high conception rates?",
                answer: "Our success comes from precise heat detection, optimal timing, premium genetics selection, and proper handling techniques. We use proven scientific methods refined through years of field experience, not guesswork or shortcuts."
              },
              {
                question: "Do you really offer 24/7 emergency service?",
                answer: "Yes! Cows don't follow business hours, and neither do we. Our emergency response team can be at your farm within 2 hours for time-sensitive breeding situations, including weekends and holidays."
              },
              {
                question: "What's included in your breeding analytics service?",
                answer: "We provide comprehensive monthly reports including conception rates, genetic progress tracking, cost analysis, and ROI calculations. You'll get clear data on how your breeding program is performing and recommendations for optimization."
              },
              {
                question: "Can you help improve our current breeding program?",
                answer: "Absolutely! We analyze your existing program, identify improvement opportunities, and implement changes gradually. Whether you need better heat detection, genetic diversity, or procedural improvements, we develop a customized plan for your operation."
              },
              {
                question: "How far do you travel for service calls?",
                answer: "We serve the greater Edmonton area and surrounding regions. Contact us to confirm service availability in your specific location—we're often willing to travel further for the right partnership."
              },
              {
                question: "What training do you provide for our farm staff?",
                answer: "We offer on-farm workshops covering heat detection techniques, proper handling procedures, record keeping systems, and breeding program management. Our goal is to make your entire team more effective between our visits."
              },
              {
                question: "How does your AI compare to that other AI everyone's talking about?",
                answer: "Great question! While ChatGPT might write you a poem about cows, our AI actually creates cows. Tech AI promises to revolutionize industries someday—our AI has been revolutionizing farms for over a decade with measurable results you can take to the bank."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-amber-100 transition-colors duration-200"
                >
                  <span className="text-lg font-semibold text-gray-900">{faq.question}</span>
                  <SafeIcon 
                    icon={FiChevronDown} 
                    className={`w-6 h-6 text-amber-600 transform transition-transform duration-200 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-8 pb-6"
                  >
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-amber-600 to-orange-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Boost Your Breeding Success?
            </h2>
            <p className="text-xl text-amber-100 max-w-3xl mx-auto">
              Let's discuss how our proven AI services can improve your herd's genetics and your farm's profitability. 
              No sales pitches, just honest advice from dairy industry experts.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Get in Touch</h3>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiPhone} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">Phone</div>
                      <div className="text-amber-100">(780) 667-0716</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiMail} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">Email</div>
                      <div className="text-amber-100">daytona@cachecow.farm</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiMapPin} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">Service Area</div>
                      <div className="text-amber-100">Edmonton & Surrounding Region</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiClock} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">Emergency Service</div>
                      <div className="text-amber-100">24/7 On-Call Response</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Why Choose Us?</h3>
                <ul className="space-y-3 text-amber-100">
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-300" />
                    <span>10+ years dairy industry expertise</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-300" />
                    <span>95% conception rate guarantee</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-300" />
                    <span>24/7 emergency response</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-300" />
                    <span>Transparent, honest pricing</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Schedule Your Consultation</h3>
              
              {/* Status Messages */}
              {submitStatus && (
                <div className={`p-4 rounded-lg mb-6 ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  <div className="flex items-center">
                    <SafeIcon 
                      icon={submitStatus.type === 'success' ? FiCheck : FiX} 
                      className={`w-5 h-5 mr-2 ${submitStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`} 
                    />
                    <span className="font-medium">{submitStatus.message}</span>
                  </div>
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Your first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Your last name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Farm Name
                  </label>
                  <input
                    type="text"
                    name="farmName"
                    value={formData.farmName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Your farm name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="(780) 667-0716"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Herd Size
                  </label>
                  <select 
                    name="herdSize"
                    value={formData.herdSize}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="">Select herd size</option>
                    <option value="1-25">1-25 cows</option>
                    <option value="26-50">26-50 cows</option>
                    <option value="51-100">51-100 cows</option>
                    <option value="101-200">101-200 cows</option>
                    <option value="200+">200+ cows</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Interest
                  </label>
                  <select 
                    name="serviceInterest"
                    value={formData.serviceInterest}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="">Select primary interest</option>
                    <option value="ai-service">AI Service</option>
                    <option value="genetic-consultation">Genetic Consultation</option>
                    <option value="breeding-program">Complete Breeding Program</option>
                    <option value="emergency-service">Emergency Service Setup</option>
                    <option value="staff-training">Staff Training</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Tell us about your current breeding program and goals..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full font-semibold py-4 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed text-white' 
                      : 'bg-amber-600 hover:bg-amber-700 text-white'
                  }`}
                >
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">🐄</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">Cache Cow AI</h3>
                  <p className="text-sm text-gray-400">Artificial Insemination Services</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4 max-w-md">
                Professional artificial insemination services for Alberta farms. 
                The original AI that actually delivers results you can count on.
              </p>
              <div className="text-sm text-gray-400">
                <p>&copy; 2024 Cache Cow AI Services. All rights reserved.</p>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-300">
                <li>Artificial Insemination</li>
                <li>Genetic Consultation</li>
                <li>Heat Detection</li>
                <li>Breeding Analytics</li>
                <li>Emergency Service</li>
                <li>Staff Training</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center space-x-2">
                  <SafeIcon icon={FiPhone} className="w-4 h-4" />
                  <span>(780) 667-0716</span>
                </li>
                <li className="flex items-center space-x-2">
                  <SafeIcon icon={FiMail} className="w-4 h-4" />
                  <span>daytona@cachecow.farm</span>
                </li>
                <li className="flex items-center space-x-2">
                  <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                  <span>Leduc County & Area</span>
                </li>
                <li className="flex items-center space-x-2">
                  <SafeIcon icon={FiShield} className="w-4 h-4" />
                  <span>24/7 Emergency Service</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>
              Proudly serving Alberta farmers with the AI that actually matters. 
              <span className="text-amber-400"> No algorithms harmed in the making of this website.</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;